import Vue from 'vue';
import { assert } from '../../assert';

export type AutoTextResizerPropertyInterface = {
    minSize: number;
    maxSize: number;
    maxTextHeight: number | null;
    ellipsis: boolean;
    checkAllChildren: boolean;
    checkWidthOverflow: boolean;
};

/**
 * Vue component to be used for text blocks that needs to resized based on size
 * of the parent div
 * @see {@link https://vuejs.org/v2/guide/components.html|Vue Components}
 * @example
 * ```html
 * <auto-text-resizer :property="textProperties" :resize="true">
 *    Text to be resized
 * </auto-text-resizer>
 * ```
 */
export const AutoTextResizer = Vue.extend({
    /**
     * Array of component properties
     */
    props: {
        property: Object as () => AutoTextResizerPropertyInterface,
        resize: Boolean,
    },

    /**
     * Template string
     */
    template: '<p><slot></slot></p>',

    /**
     * Called after the instance has been mounted
     */
    mounted: function () {
        if (this.resize) {
            this.adjustFontSize();
        }
    },

    /**
     * Called after a data change causes the virtual DOM to be re-rendered and
     * patched.
     */
    updated: function () {
        if (this.resize) {
            this.adjustFontSize();
        }
    },

    /**
     * Watchers for properties
     */
    watch: {
        /**
         * Function to run when value of resize property changes
         */
        resize: function (): void {
            if (this.resize) {
                this.adjustFontSize();
            }
        },
    },

    /**
     * Component methods
     */
    methods: {
        /**
         * Resize the font size based on minSize, maxSize and resize properties
         */
        adjustFontSize: function (): void {
            const element = this.$el;
            assert(element instanceof HTMLElement, 'Must be an HTMLElement');
            // Get just the number of the font size.
            const elementFontSize = parseInt(element.style.fontSize);
            // Try to get the computed font size from CSS.
            const computedFontSize = parseInt(
                getComputedStyle(element, null).getPropertyValue('font-size')
            );

            // Default to the min font size allowed.
            let startingFontSize = this.property.minSize;
            // If the element font size exists and is larger than the
            // minimum set that.
            if (!isNaN(elementFontSize) && elementFontSize > startingFontSize) {
                startingFontSize = elementFontSize;
            }
            // If the computed font size exists and is larger than the
            // minimum and element size set that.
            if (!isNaN(computedFontSize) && computedFontSize > startingFontSize) {
                startingFontSize = computedFontSize;
            }
            // If the computed or element or min is larger than the
            // max then use the max.
            if (startingFontSize > this.property.maxSize) {
                startingFontSize = this.property.maxSize;
            }

            element.style.fontSize = `${startingFontSize}px`;
            //Set max height so that the font does not cause the div to expand
            //too much
            element.style.maxHeight = `${this.property.maxTextHeight}px`;
            element.style.lineHeight = '1.2em';

            if (element.clientHeight === 0) return;

            // initialize i to avoid infinite loop
            let i = 0;
            // While the element is NOT overflowing increment the font size.
            while (!this.isOverflowing(element)) {
                i++;
                if (i > 50) {
                    break;
                }

                // The fontSize cannot be less than minSize
                let adjustedFontSize = parseInt(element.style.fontSize);

                if (adjustedFontSize < this.property.maxSize) {
                    ++adjustedFontSize;
                    element.style.fontSize = `${adjustedFontSize}px`;
                }
            }

            //Take the font down by a point to make it fit
            element.style.fontSize = `${parseInt(element.style.fontSize) - 1}px`;

            // initialize i to avoid infinite loop
            i = 0;
            // truncate post if it cannot fit into the parent and
            //   the font cannot be reduced any more
            if (this.property.ellipsis && this.isOverflowing(element)) {
                const text = (element.innerHTML + '...').split('');
                while (this.isOverflowing(element)) {
                    i++;
                    if (i > 50) {
                        break;
                    }
                    // We are splicing off one character at a time
                    //  from the end of the text ensuring we keep
                    //  the ellipsis we appended.
                    text.splice(text.length - 4, 1);
                    element.innerHTML = text.join('');
                }
            }
        },
        /**
         * Check if text is overflowing it's container
         */
        isOverflowing: function (el: HTMLElement): boolean {
            if (this.property.checkAllChildren) {
                let curOverflow;
                let clientHeight;
                let clientWidth;
                let scrollHeight;
                let scrollWidth;
                let overflowing = false;

                for (let i = 0; i < el.children.length; i++) {
                    const child = el.children[i] as HTMLElement;
                    if (!overflowing) {
                        curOverflow = child.style.overflow;

                        child.style.overflow = 'hidden';

                        clientHeight = child.clientHeight;
                        clientWidth = child.clientWidth;

                        //Internet Explorer does not calculate scrollWidth
                        //properly unless overflow is set to "visible"
                        child.style.overflow = 'visible';

                        scrollHeight = child.scrollHeight;
                        scrollWidth = child.scrollWidth;

                        //checkWidthOverflow checks if the text overflows the
                        //width and height of an element
                        if (this.property.checkWidthOverflow) {
                            overflowing = clientHeight < scrollHeight || clientWidth < scrollWidth;
                        } else {
                            overflowing = clientHeight < scrollHeight;
                        }

                        child.style.overflow = curOverflow;
                    }
                }
                return overflowing;
            }

            // If there is no parentElement.clientHeight use a default
            const maxParentHeight = el.parentElement?.clientHeight || 100;
            // If there is no parentElement.clientWidth use a default
            const maxParentWidth = el.parentElement?.clientWidth || 100;
            const overParentHeight = maxParentHeight < el.scrollHeight;
            const overParentWidth = maxParentWidth < el.scrollWidth;
            const isClientHeightOverScrollHeight = el.clientHeight < el.scrollHeight - 2;

            return overParentHeight || overParentWidth || isClientHeightOverScrollHeight;
        },
    },
});
